import React, { useState, useMemo } from "react";
import axios from "axios";
import { format } from "date-fns";
import { ar as arLocale } from "date-fns/locale";
import { useLang } from "../contexts/LanguageContext";
import { Check, ArrowRight, ArrowLeft, Calendar as CalendarIcon, MapPin, Info, Mic, MicOff, Loader2 } from "lucide-react";
import WhatsAppIcon from "./WhatsAppIcon";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WHATSAPP = "966545670477";
const TRIPEX_GREEN = "#4EC487";

// City → available experiences mapping. Static, embedded on the client.
const CITY_EXPERIENCES_DATA = {
  cities: [
    { id: "jeddah", name_ar: "جدة", name_en: "Jeddah", description: "بوابة البحر الأحمر والتراث الحجازي",
      available_experiences: ["beach", "diving", "water_sports", "heritage_culture", "entertainment", "dining", "luxury_stay", "adventure", "sports"] },
    { id: "riyadh", name_ar: "الرياض", name_en: "Riyadh", description: "عاصمة الترفيه والمغامرات الصحراوية",
      available_experiences: ["desert_safari", "heritage_culture", "entertainment", "dining", "luxury_stay", "adventure", "sports", "stargazing"] },
    { id: "alula", name_ar: "العُلا", name_en: "AlUla", description: "متحف مفتوح في الصحراء",
      available_experiences: ["heritage_culture", "desert_safari", "adventure", "stargazing", "nature_reserves", "dining", "luxury_stay", "mountain_hiking", "sports"] },
    { id: "neom", name_ar: "نيوم", name_en: "NEOM", description: "مدينة المستقبل - ساحل وجبال فاخرة",
      available_experiences: ["beach", "diving", "water_sports", "mountain_hiking", "luxury_stay", "adventure", "nature_reserves", "dining", "sports", "stargazing"] },
    { id: "abha", name_ar: "أبها", name_en: "Abha", description: "ملاذ جبلي بارد",
      available_experiences: ["mountain_hiking", "nature_reserves", "heritage_culture", "adventure", "dining", "luxury_stay", "stargazing"] },
    { id: "diriyah", name_ar: "الدرعية", name_en: "Diriyah", description: "تراث وثقافة وترفيه راقٍ",
      available_experiences: ["heritage_culture", "entertainment", "dining", "luxury_stay", "sports"] },
    { id: "tabuk", name_ar: "تبوك", name_en: "Tabuk", description: "الأكثر تنوعاً - بحر وجبال وصحراء",
      available_experiences: ["beach", "diving", "water_sports", "mountain_hiking", "desert_safari", "heritage_culture", "adventure", "nature_reserves", "stargazing", "luxury_stay"] },
    { id: "madinah", name_ar: "المدينة المنورة", name_en: "Madinah", description: "سياحة دينية وروحانية",
      available_experiences: ["heritage_culture", "dining", "religious"] },
    { id: "taif", name_ar: "الطائف", name_en: "Taif", description: "مدينة الورد والجبال",
      available_experiences: ["mountain_hiking", "nature_reserves", "heritage_culture", "dining", "adventure"] },
    { id: "eastern_province", name_ar: "المنطقة الشرقية", name_en: "Eastern Province", description: "شواطئ الخليج والترفيه العصري",
      available_experiences: ["beach", "diving", "water_sports", "entertainment", "dining", "heritage_culture", "luxury_stay"] },
    { id: "hail", name_ar: "حائل", name_en: "Hail", description: "تراث صحراوي وفنون صخرية",
      available_experiences: ["heritage_culture", "desert_safari", "adventure", "nature_reserves", "stargazing"] },
    { id: "jazan", name_ar: "جازان", name_en: "Jazan", description: "جوهرة استوائية مخفية",
      available_experiences: ["beach", "diving", "water_sports", "mountain_hiking", "nature_reserves", "heritage_culture", "adventure", "dining"] },
  ],
  experience_types: [
    { id: "beach", label_ar: "تجربة شاطئية", icon: "🏖️", description_ar: "شواطئ، سباحة، حمامات شمس" },
    { id: "diving", label_ar: "غوص", icon: "🤿", description_ar: "غوص، سنوركل، استكشاف شعاب مرجانية" },
    { id: "water_sports", label_ar: "رياضات مائية", icon: "🏄", description_ar: "جت سكي، كاياك، تزلج على الماء" },
    { id: "mountain_hiking", label_ar: "مشي جبلي", icon: "🏔️", description_ar: "مشي، تسلق، تلفريك، إطلالات جبلية" },
    { id: "desert_safari", label_ar: "سفاري صحراوية", icon: "🏜️", description_ar: "رحلات صحراوية، كثبان رملية، تطعيس" },
    { id: "heritage_culture", label_ar: "تراث وثقافة", icon: "🏛️", description_ar: "مواقع أثرية، متاحف، أحياء تاريخية" },
    { id: "entertainment", label_ar: "ترفيه وفعاليات", icon: "🎭", description_ar: "حفلات، ملاهي، مواسم، فعاليات" },
    { id: "dining", label_ar: "تجارب غذائية", icon: "🍽️", description_ar: "مطاعم فاخرة، جولات طعام، تجارب محلية" },
    { id: "luxury_stay", label_ar: "إقامة فاخرة", icon: "🏨", description_ar: "منتجعات، فنادق بوتيك، تجارب إقامة مميزة" },
    { id: "adventure", label_ar: "مغامرات", icon: "🚁", description_ar: "هليكوبتر، سيارات 4x4، زيب لاين، بالون طائر" },
    { id: "sports", label_ar: "رياضات", icon: "🎯", description_ar: "غولف، رماية، فروسية، رياضات متنوعة" },
    { id: "nature_reserves", label_ar: "تجارب طبيعية", icon: "🌿", description_ar: "محميات، أودية، شلالات، غابات" },
    { id: "stargazing", label_ar: "مراقبة النجوم", icon: "⭐", description_ar: "سماء مظلمة، تجارب فلكية، نوم تحت النجوم" },
    { id: "religious", label_ar: "سياحة دينية", icon: "🕌", description_ar: "زيارات دينية، مساجد تاريخية، مواقع إسلامية" },
  ],
};

const initial = {
  full_name: "",
  phone: "",
  email: "",
  city: "",
  city_id: "",           // ← id from CITY_EXPERIENCES_DATA.cities, drives experience filtering
  experience_ids: [],    // ← ids from CITY_EXPERIENCES_DATA.experience_types (new)
  goal: "",
  services: [],
  travel_dates: "",
  travel_range: null, // { from: Date, to: Date }
  group_size: "",
  budget: "",
  notes: "",
};

export default function Onboarding({ presetCity }) {
  const { t, isAr, lang } = useLang();
  const [step, setStep] = useState(0);
  const [data, setData] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  // Inline "options updated" notice — shown briefly when auto-deselecting experiences after a city change.
  const [cityUpdateNotice, setCityUpdateNotice] = useState(false);

  React.useEffect(() => {
    if (presetCity && !data.city) setData((d) => ({ ...d, city: presetCity }));
    // eslint-disable-next-line
  }, [presetCity]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggleService = (s) => setData((d) => ({
    ...d, services: d.services.includes(s) ? d.services.filter((x) => x !== s) : [...d.services, s]
  }));

  // Pick a city in step 2 (experiences step). Auto-deselects any previously
  // chosen experiences that aren't available in the new city and shows a
  // brief inline notice so the user understands why their selections cleared.
  const selectCity = (cityId) => {
    setData((d) => {
      const target = CITY_EXPERIENCES_DATA.cities.find((c) => c.id === cityId);
      if (!target) return d;
      const nextAllowed = new Set(target.available_experiences);
      const pruned = d.experience_ids.filter((id) => nextAllowed.has(id));
      const dropped = pruned.length !== d.experience_ids.length;
      if (dropped) {
        setCityUpdateNotice(true);
        setTimeout(() => setCityUpdateNotice(false), 3200);
      }
      return {
        ...d,
        city_id: cityId,
        city: isAr ? target.name_ar : target.name_en, // keep legacy `city` in sync for WhatsApp msg + lead
        experience_ids: pruned,
      };
    });
  };

  const toggleExperience = (expId) => setData((d) => ({
    ...d,
    experience_ids: d.experience_ids.includes(expId)
      ? d.experience_ids.filter((x) => x !== expId)
      : [...d.experience_ids, expId],
  }));

  // Experience cards to render in step 2 — filtered by the currently selected city.
  const visibleExperiences = useMemo(() => {
    if (!data.city_id) return [];
    const city = CITY_EXPERIENCES_DATA.cities.find((c) => c.id === data.city_id);
    if (!city) return [];
    const allowed = new Set(city.available_experiences);
    return CITY_EXPERIENCES_DATA.experience_types.filter((e) => allowed.has(e.id));
  }, [data.city_id]);

  const goals = [
    { key: "goal_romantic", icon: "💍" },
    { key: "goal_adventure", icon: "⛰" },
    { key: "goal_family", icon: "👨‍👩‍👧" },
    { key: "goal_vip", icon: "✦" },
    { key: "goal_culture", icon: "🏛" },
    { key: "goal_relax", icon: "🌿" },
  ];

  const services = [
    "svc_transport", "svc_stay", "svc_dining", "svc_yacht", "svc_tours", "svc_vip",
  ];

  const cities = isAr
    ? ["الرياض", "جدة", "الشرقية", "العُلا", "عسير", "أخرى"]
    : ["Riyadh", "Jeddah", "Eastern Province", "AlUla", "Asir", "Other"];

  const steps = [t("onb_step1"), t("onb_step2"), t("onb_step3"), t("onb_step4")];

  const canNext = () => {
    if (step === 0) return data.full_name.trim().length > 1 && data.phone.trim().length > 5;
    if (step === 1) return !!data.goal && !!data.city;
    if (step === 2) return !!data.city_id && data.experience_ids.length > 0;
    return true;
  };

  const buildWaMessage = () => {
    const goalLabel = goals.find((g) => g.key === data.goal);
    const servicesLabels = data.services.map((s) => t(s)).join(", ");
    // Also include the new detailed city + experience choices from step 2.
    const cityObj = CITY_EXPERIENCES_DATA.cities.find((c) => c.id === data.city_id);
    const cityDetailed = cityObj ? (isAr ? cityObj.name_ar : cityObj.name_en) : "";
    const experiencesDetailed = data.experience_ids
      .map((id) => {
        const e = CITY_EXPERIENCES_DATA.experience_types.find((x) => x.id === id);
        return e ? `${e.icon} ${e.label_ar}` : id;
      })
      .join("، ");
    if (isAr) {
      return (
`السلام عليكم 👋
أنا ${data.full_name}، حابب أرتّب تجربة مع تريبيكس.

• المدينة: ${cityDetailed || data.city}
• الهدف: ${goalLabel ? t(goalLabel.key) : "-"}
• التجارب المطلوبة: ${experiencesDetailed || "-"}
• الخدمات: ${servicesLabels || "-"}
• التاريخ: ${data.travel_dates || "-"}
• عدد الأشخاص: ${data.group_size || "-"}
• الميزانية: ${data.budget || "-"}
• ملاحظات: ${data.notes || "-"}

جوالي: ${data.phone}${data.email ? "\nإيميل: " + data.email : ""}

— من الموقع`
      );
    }
    return (
`Hi 👋
I'm ${data.full_name}, I'd like to arrange a Tripex experience.

• City: ${cityDetailed || data.city}
• Goal: ${goalLabel ? t(goalLabel.key) : "-"}
• Experiences requested: ${experiencesDetailed || "-"}
• Services: ${servicesLabels || "-"}
• Dates: ${data.travel_dates || "-"}
• Group size: ${data.group_size || "-"}
• Budget: ${data.budget || "-"}
• Notes: ${data.notes || "-"}

Phone: ${data.phone}${data.email ? "\nEmail: " + data.email : ""}

— من الموقع`
    );
  };

  const submit = () => {
    if (submitting) return;
    setSubmitting(true);

    // 1) Build the WhatsApp URL SYNCHRONOUSLY inside the user-click handler.
    //    Any await before opening WhatsApp would break the user-gesture context
    //    on mobile browsers and cause popup/redirect to be blocked.
    const msg = encodeURIComponent(buildWaMessage());
    const waUrl = `https://wa.me/${WHATSAPP}?text=${msg}`;

    // 2) Fire-and-forget the lead capture. Do NOT await it — backend
    //    persistence must never block the WhatsApp hand-off.
    axios.post(`${API}/leads`, { ...data, lang }).catch(() => {});

    // 3) Redirect to WhatsApp. We use location.href so the OS can intercept
    //    `wa.me` and launch the WhatsApp app on mobile. Fallback to window.open
    //    if location assignment somehow fails.
    try {
      window.location.href = waUrl;
    } catch (e) {
      window.open(waUrl, "_blank", "noopener,noreferrer");
    }
    // Reset submitting flag in case the user comes back via browser back button
    setTimeout(() => setSubmitting(false), 1500);
  };

  const FwdIcon = isAr ? ArrowLeft : ArrowRight;
  const BackIcon = isAr ? ArrowRight : ArrowLeft;

  return (
    <section id="start" className="reveal relative py-20 md:py-28 overflow-hidden">
      <div className="relative z-10 mx-auto max-w-5xl px-5">
        <div className="mb-10 md:mb-14 text-center">
          <h2
            className={`text-emerald-deep text-balance mb-3 ${isAr ? 'font-display-ar text-4xl sm:text-5xl' : 'font-display-en text-4xl sm:text-5xl'}`}
            data-testid="onb-title"
          >
            {t("onb_title")}
          </h2>
          <p className="text-emerald-deep/70 max-w-2xl mx-auto text-base md:text-lg"
             style={{ fontFamily: isAr ? 'Cairo' : 'Plus Jakarta Sans' }}>
            {t("onb_sub")}
          </p>
        </div>

        <div className="glass rounded-[2rem] p-5 md:p-10" data-testid="onboarding-card">
          {/* Stepper */}
          <div className="flex items-center justify-between mb-8 md:mb-10 gap-2">
            {steps.map((label, i) => (
              <React.Fragment key={i}>
                <div className="flex flex-col items-center gap-2 min-w-0">
                  <div
                    className={`step-dot ${i < step ? 'done' : i === step ? 'active' : 'pending'}`}
                    data-testid={`step-dot-${i}`}
                  >
                    {i < step ? <Check size={16} /> : i + 1}
                  </div>
                  <div className={`text-[10px] md:text-xs font-semibold tracking-wide whitespace-nowrap ${i === step ? 'text-emerald-deep' : 'text-emerald-deep/55'}`}
                       style={{ fontFamily: isAr ? 'Cairo' : 'Plus Jakarta Sans' }}>
                    {label}
                  </div>
                </div>
                {i < steps.length - 1 && (
                  <div className="flex-1 h-[2px] mx-1 bg-sage/30 rounded-full overflow-hidden">
                    <div className={`h-full bg-gradient-to-r from-emerald via-gold to-clay transition-all duration-700 ${i < step ? 'w-full' : 'w-0'}`} />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Step content */}
          <div className="min-h-[280px]" dir={isAr ? 'rtl' : 'ltr'}>
            {step === 0 && (
              <div className="space-y-4 animate-fade-up" data-testid="step-0">
                <Field label={t("onb_name")} testId="onb-name">
                  <input className="onb-input" value={data.full_name} onChange={(e) => set("full_name", e.target.value)} />
                </Field>
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label={t("onb_phone")} testId="onb-phone">
                    <input className="onb-input" type="tel" value={data.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+966 5x xxx xxxx" />
                  </Field>
                  <Field label={t("onb_email")} testId="onb-email">
                    <input className="onb-input" type="email" value={data.email} onChange={(e) => set("email", e.target.value)} />
                  </Field>
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-5 animate-fade-up" data-testid="step-1">
                <div>
                  <Label>{t("onb_city")}</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {cities.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => set("city", c)}
                        data-testid={`onb-city-${c}`}
                        className={`px-4 py-2 rounded-full text-sm border transition ${data.city === c ? 'bg-emerald-deep text-cream border-emerald-deep' : 'bg-cream-light border-sage/40 text-emerald-deep hover:bg-sage/20'}`}
                        style={{ fontFamily: isAr ? 'Cairo' : 'Plus Jakarta Sans' }}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>{t("onb_goal")}</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
                    {goals.map((g) => (
                      <button
                        key={g.key}
                        type="button"
                        onClick={() => set("goal", g.key)}
                        data-testid={`onb-goal-${g.key}`}
                        className={`rounded-2xl p-4 border text-start transition ${data.goal === g.key ? 'bg-emerald-deep text-cream border-emerald-deep' : 'bg-cream-light/70 border-sage/40 text-emerald-deep hover:bg-sage/20'}`}
                      >
                        <div className="text-xl mb-1">{g.icon}</div>
                        <div className="font-semibold text-sm md:text-base"
                             style={{ fontFamily: isAr ? 'Cairo' : 'Plus Jakarta Sans' }}>
                          {t(g.key)}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="animate-fade-up space-y-6" data-testid="step-2">
                {/* City selector — always the first thing on the experiences step */}
                <div>
                  <Label>
                    <span className="flex items-center gap-2">
                      <MapPin size={16} style={{ color: TRIPEX_GREEN }} />
                      {isAr ? "اختر المدينة أولاً" : "Choose the city first"}
                    </span>
                  </Label>
                  <div className="mt-2">
                    <select
                      value={data.city_id}
                      onChange={(e) => selectCity(e.target.value)}
                      data-testid="onb-city-select"
                      className="city-select w-full"
                      dir={isAr ? "rtl" : "ltr"}
                      style={{ fontFamily: isAr ? 'Cairo' : 'Plus Jakarta Sans' }}
                    >
                      <option value="">
                        {isAr ? "— اختر المدينة —" : "— Choose a city —"}
                      </option>
                      {CITY_EXPERIENCES_DATA.cities.map((c) => (
                        <option key={c.id} value={c.id} data-testid={`onb-city-opt-${c.id}`}>
                          {isAr ? `${c.name_ar} — ${c.description}` : `${c.name_en} — ${c.description}`}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Inline "options updated" notice — shown briefly after auto-deselection */}
                  {cityUpdateNotice && (
                    <div
                      data-testid="city-update-notice"
                      className="mt-3 flex items-start gap-2 rounded-xl px-3 py-2 text-xs animate-fade-up"
                      style={{
                        background: 'rgba(78,196,135,0.12)',
                        border: '1px solid rgba(78,196,135,0.35)',
                        color: '#0f2421',
                        fontFamily: isAr ? 'Cairo' : 'Plus Jakarta Sans',
                      }}
                    >
                      <Info size={14} style={{ color: TRIPEX_GREEN, marginTop: 2, flexShrink: 0 }} />
                      <span>{isAr ? "تم تحديث الخيارات المتاحة حسب المدينة" : "Options updated based on the city"}</span>
                    </div>
                  )}
                </div>

                {/* Experiences — either the empty-state prompt or the filtered grid */}
                {!data.city_id ? (
                  <div
                    data-testid="onb-experiences-empty"
                    className="rounded-2xl px-5 py-8 text-center"
                    style={{
                      background: 'rgba(78,196,135,0.06)',
                      border: '1px dashed rgba(78,196,135,0.45)',
                      color: '#0f2421',
                      fontFamily: isAr ? 'Cairo' : 'Plus Jakarta Sans',
                    }}
                  >
                    <div className="text-2xl mb-2">📍</div>
                    <div className="text-sm md:text-base font-semibold">
                      {isAr ? "اختر المدينة لعرض التجارب المتاحة" : "Choose a city to see available experiences"}
                    </div>
                  </div>
                ) : (
                  <div>
                    <Label>
                      {isAr ? "التجارب المتاحة" : "Available experiences"}
                      <span className="text-emerald-deep/50 font-normal text-xs ms-2">
                        ({visibleExperiences.length})
                      </span>
                    </Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3" data-testid="onb-experiences-grid">
                      {visibleExperiences.map((exp, idx) => {
                        const selected = data.experience_ids.includes(exp.id);
                        return (
                          <button
                            key={exp.id}
                            type="button"
                            onClick={() => toggleExperience(exp.id)}
                            data-testid={`onb-exp-${exp.id}`}
                            className={`exp-card group text-start ${selected ? 'exp-card-selected' : ''}`}
                            style={{ animationDelay: `${idx * 40}ms` }}
                          >
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <div className="text-2xl leading-none">{exp.icon}</div>
                              {selected && (
                                <div
                                  className="rounded-full w-6 h-6 flex items-center justify-center text-white shrink-0"
                                  style={{ background: TRIPEX_GREEN }}
                                  aria-hidden
                                >
                                  <Check size={14} strokeWidth={3} />
                                </div>
                              )}
                            </div>
                            <div className="font-bold text-sm md:text-base mb-0.5"
                                 style={{ fontFamily: isAr ? 'Cairo' : 'Plus Jakarta Sans' }}>
                              {exp.label_ar}
                            </div>
                            <div className="text-xs opacity-70 leading-relaxed"
                                 style={{ fontFamily: isAr ? 'Cairo' : 'Plus Jakarta Sans' }}>
                              {exp.description_ar}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4 animate-fade-up" data-testid="step-3">
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label={t("onb_dates")} testId="onb-dates">
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          data-testid="onb-dates-trigger"
                          className="onb-input flex items-center justify-between gap-2 text-start cursor-pointer hover:border-emerald transition"
                        >
                          <span className={data.travel_dates ? "text-emerald-deep" : "text-emerald-deep/40"}>
                            {data.travel_dates || (isAr ? "اختر تاريخ الرحلة" : "Select travel dates")}
                          </span>
                          <CalendarIcon size={18} className="text-emerald-soft shrink-0" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-auto p-0 bg-cream-light border border-sage/50 shadow-2xl rounded-2xl overflow-hidden"
                        align="start"
                        data-testid="onb-dates-popover"
                      >
                        <Calendar
                          mode="range"
                          numberOfMonths={1}
                          selected={data.travel_range || undefined}
                          onSelect={(range) => {
                            set("travel_range", range || null);
                            if (range?.from && range?.to) {
                              const locale = isAr ? arLocale : undefined;
                              const label = `${format(range.from, "d MMM", { locale })} – ${format(range.to, "d MMM yyyy", { locale })}`;
                              set("travel_dates", label);
                            } else if (range?.from) {
                              const locale = isAr ? arLocale : undefined;
                              set("travel_dates", format(range.from, "d MMM yyyy", { locale }));
                            } else {
                              set("travel_dates", "");
                            }
                          }}
                          disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))}
                          dir={isAr ? "rtl" : "ltr"}
                          locale={isAr ? arLocale : undefined}
                          className="font-cairo tripex-calendar"
                          initialFocus
                        />
                        {data.travel_range?.from && (
                          <div className="px-4 pb-3 pt-1 flex items-center justify-between border-t border-sage/30 bg-cream-light/80">
                            <button
                              type="button"
                              onClick={() => { set("travel_range", null); set("travel_dates", ""); }}
                              data-testid="onb-dates-clear"
                              className="text-xs text-emerald-deep/70 hover:text-emerald-deep font-semibold underline"
                              style={{ fontFamily: isAr ? 'Cairo' : 'Plus Jakarta Sans' }}
                            >
                              {isAr ? "مسح" : "Clear"}
                            </button>
                            <span className="text-xs text-emerald-deep font-semibold"
                                  style={{ fontFamily: isAr ? 'Cairo' : 'Plus Jakarta Sans' }}>
                              {data.travel_dates}
                            </span>
                          </div>
                        )}
                      </PopoverContent>
                    </Popover>
                  </Field>
                  <Field label={t("onb_group")} testId="onb-group">
                    <input className="onb-input" value={data.group_size} onChange={(e) => set("group_size", e.target.value)} placeholder={isAr ? "مثال: 2 بالغ" : "e.g. 2 adults"} />
                  </Field>
                </div>
                <Field label={t("onb_budget")} testId="onb-budget">
                  <input className="onb-input" value={data.budget} onChange={(e) => set("budget", e.target.value)} placeholder={isAr ? "مثال: 15,000 ريال" : "e.g. SAR 15,000"} />
                </Field>
                <Field label={t("onb_notes")} testId="onb-notes">
                  <textarea
                    className="onb-input min-h-[100px]"
                    rows={3}
                    value={data.notes}
                    onChange={(e) => set("notes", e.target.value)}
                    data-testid="onb-notes-textarea"
                  />
                  <VoiceRecorder
                    isAr={isAr}
                    onTranscribed={(text) => {
                      // Append to existing notes (space-separated) so multiple recordings stack.
                      setData((d) => ({ ...d, notes: d.notes ? `${d.notes.trim()} ${text}` : text }));
                    }}
                  />
                </Field>
              </div>
            )}
          </div>

          {/* Footer controls */}
          <div className="flex items-center justify-between mt-8 pt-5 border-t border-sage/30">
            <button
              onClick={() => setStep(Math.max(0, step - 1))}
              disabled={step === 0}
              data-testid="onb-back-btn"
              className="btn-ghost !py-2 !px-5 text-sm disabled:opacity-30"
              style={{ fontFamily: isAr ? 'Cairo' : 'Plus Jakarta Sans' }}
            >
              <BackIcon size={16} className="inline mx-1" />
              {t("onb_back")}
            </button>
            {step < steps.length - 1 ? (
              <button
                onClick={() => canNext() && setStep(step + 1)}
                disabled={!canNext()}
                data-testid="onb-next-btn"
                className="btn-primary !py-2.5 !px-6 text-sm disabled:opacity-40"
                style={{ fontFamily: isAr ? 'Cairo' : 'Plus Jakarta Sans' }}
              >
                {t("onb_next")}
                <FwdIcon size={16} />
              </button>
            ) : (
              <button
                onClick={submit}
                disabled={submitting}
                data-testid="onb-submit-btn"
                className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-bold hover:opacity-90 active:scale-95 transition disabled:opacity-50 shadow-lg"
                style={{ fontFamily: isAr ? 'Cairo' : 'Plus Jakarta Sans', background: '#25D366', color: '#FFFFFF' }}
              >
                <WhatsAppIcon size={18} color="#FFFFFF" />
                {t("onb_submit")}
              </button>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .onb-input {
          width: 100%;
          background: rgba(250,248,240, 0.85);
          border: 1px solid rgba(196,153,232, 0.45);
          border-radius: 14px;
          padding: 12px 16px;
          color: #1a1a1a;
          font-family: ${isAr ? "'Cairo'" : "'Plus Jakarta Sans'"}, sans-serif;
          transition: all 0.25s ease;
        }
        .onb-input:focus { outline: none; border-color: #51BDB0; background: #FFFFFF; }
        .onb-input::placeholder { color: rgba(15,36,33, 0.4); }

        /* City dropdown — full-width select styled to match Tripex glassy inputs */
        .city-select {
          appearance: none;
          -webkit-appearance: none;
          background: rgba(255,255,255,0.9);
          border: 1.5px solid rgba(78,196,135,0.35);
          border-radius: 14px;
          padding: 13px 18px;
          padding-${isAr ? 'left' : 'right'}: 44px;
          color: #0f2421;
          font-weight: 600;
          font-size: 15px;
          cursor: pointer;
          transition: all 0.25s ease;
          background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%234EC487' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'/%3e%3c/svg%3e");
          background-repeat: no-repeat;
          background-position: ${isAr ? 'left 16px' : 'right 16px'} center;
        }
        .city-select:hover { border-color: #4EC487; background-color: #FFFFFF; }
        .city-select:focus {
          outline: none;
          border-color: #4EC487;
          box-shadow: 0 0 0 4px rgba(78,196,135,0.15);
          background-color: #FFFFFF;
        }
        .city-select option { color: #0f2421; padding: 10px; }

        /* Experience card — smooth fade-up entrance + Tripex green selected state */
        .exp-card {
          position: relative;
          background: rgba(255,255,255,0.85);
          border: 1.5px solid rgba(196,153,232,0.35);
          border-radius: 18px;
          padding: 14px 16px;
          color: #0f2421;
          transition: transform .22s ease, border-color .22s ease, box-shadow .22s ease, background .22s ease;
          opacity: 0;
          animation: expFadeUp .4s cubic-bezier(.2,.7,.2,1) forwards;
          text-align: ${isAr ? 'right' : 'left'};
          cursor: pointer;
        }
        .exp-card:hover {
          border-color: #4EC487;
          box-shadow: 0 8px 22px -10px rgba(78,196,135,0.35);
          transform: translateY(-2px);
        }
        .exp-card-selected {
          border-color: #4EC487 !important;
          background: linear-gradient(135deg, rgba(78,196,135,0.14), rgba(78,196,135,0.05)) !important;
          box-shadow: 0 8px 22px -8px rgba(78,196,135,0.45);
        }
        @keyframes expFadeUp {
          0% { opacity: 0; transform: translateY(10px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}

const Field = ({ label, children, testId }) => (
  <label className="block" data-testid={testId}>
    <Label>{label}</Label>
    <div className="mt-1.5">{children}</div>
  </label>
);

const Label = ({ children }) => (
  <span className="text-sm font-semibold text-emerald-deep/85">{children}</span>
);

// ── VoiceRecorder ─────────────────────────────────────────────────────
// Website intake form voice-note capture. Uses MediaRecorder to record up to
// 60s of audio (webm/opus in most browsers), POSTs it to /api/transcribe-audio,
// and appends the transcribed text to the parent's notes field via onTranscribed.
// Matches Tripex's dark/gold-accented luxury aesthetic.
const MAX_RECORDING_MS = 60_000;

function VoiceRecorder({ isAr, onTranscribed }) {
  const [state, setState] = React.useState("idle"); // idle | prepping | recording | transcribing | error
  const [errorMsg, setErrorMsg] = React.useState("");
  const [elapsed, setElapsed] = React.useState(0);
  // Show the pre-permission tooltip on the very first click of the session,
  // then skip it on subsequent recordings so power-users aren't nagged.
  const [permissionPrimed, setPermissionPrimed] = React.useState(false);
  const recorderRef = React.useRef(null);
  const chunksRef = React.useRef([]);
  const streamRef = React.useRef(null);
  const timerRef = React.useRef(null);
  const autoStopRef = React.useRef(null);

  const cleanupStream = () => {
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch (_) {}
    streamRef.current = null;
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (autoStopRef.current) { clearTimeout(autoStopRef.current); autoStopRef.current = null; }
  };

  React.useEffect(() => () => cleanupStream(), []);

  const startRecording = async () => {
    setErrorMsg("");
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setState("error");
      setErrorMsg(isAr ? "تعذّر تسجيل الصوت، اكتب رسالتك مباشرةً." : "Voice recording isn't supported here — please type your message.");
      return;
    }
    // First-time UX: warn the user we're about to ask for mic access,
    // then trigger getUserMedia after a short beat. Subsequent recordings
    // skip this priming step so they feel snappy.
    if (!permissionPrimed) {
      setState("prepping");
      setPermissionPrimed(true);
      setTimeout(() => { doGetUserMedia(); }, 1400);
      return;
    }
    doGetUserMedia();
  };

  const doGetUserMedia = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      // Pick the best available mime type — webm/opus works in Chrome/Edge/Firefox,
      // Safari falls back to whatever it supports.
      let mimeType = "";
      const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];
      for (const t of candidates) {
        if (MediaRecorder.isTypeSupported?.(t)) { mimeType = t; break; }
      }
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      recorderRef.current = recorder;
      recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        cleanupStream();
        if (!chunksRef.current.length) {
          setState("idle");
          return;
        }
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" });
        chunksRef.current = [];
        setState("transcribing");
        try {
          const fd = new FormData();
          const ext = (recorder.mimeType || "").includes("mp4") ? "m4a" : (recorder.mimeType || "").includes("ogg") ? "ogg" : "webm";
          fd.append("file", blob, `intake_voice.${ext}`);
          const resp = await axios.post(`${API}/transcribe-audio`, fd, {
            timeout: 90_000,
            // Do NOT set Content-Type — browser auto-adds the multipart boundary.
          });
          const text = (resp.data?.text || "").trim();
          if (!text) throw new Error("empty transcription");
          onTranscribed(text);
          setState("idle");
        } catch (err) {
          setState("error");
          setErrorMsg(isAr ? "تعذّر تسجيل الصوت، اكتب رسالتك مباشرةً." : "Voice transcription failed — please type your message.");
        }
      };
      recorder.start();
      setState("recording");
      setElapsed(0);
      const startTs = Date.now();
      timerRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - startTs) / 1000)), 250);
      autoStopRef.current = setTimeout(() => {
        if (recorderRef.current?.state === "recording") recorderRef.current.stop();
      }, MAX_RECORDING_MS);
    } catch (err) {
      cleanupStream();
      setState("error");
      // Softer message for permission denial — the guide subtitle already
      // reminds them Dana accepts text too, so keep this reassuring.
      setErrorMsg(isAr ? "يمكنك كتابة رسالتك بدلاً من ذلك." : "You can type your message instead.");
    }
  };

  const stopRecording = () => {
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
  };

  const busy = state === "transcribing";
  const recording = state === "recording";
  const prepping = state === "prepping";

  return (
    <div className="mt-2" data-testid="voice-recorder">
      {/* Bilingual guide subtitle — always shown, muted gold/beige tone. */}
      <div className="voice-guide" data-testid="voice-guide">
        <span className="voice-guide-line" dir="rtl" lang="ar">راسلي دانا أو أرسل لها رسالة صوتية وستساعدك في توفير التجربة التي تريدها</span>
        <span className="voice-guide-line" dir="ltr" lang="en">Text Dana or send her a voice note and she will provide you with the experience you want</span>
      </div>

      <div className="flex items-center gap-3 mt-2">
        <button
          type="button"
          onClick={recording ? stopRecording : startRecording}
          disabled={busy || prepping}
          data-testid={recording ? "voice-stop-btn" : "voice-record-btn"}
          className={`voice-btn ${recording ? 'voice-btn-recording' : ''} ${(busy || prepping) ? 'voice-btn-busy' : ''}`}
          aria-label={recording ? (isAr ? "إيقاف التسجيل" : "Stop recording") : (isAr ? "بدء التسجيل" : "Start recording")}
        >
          {(busy || prepping) ? (
            <Loader2 size={16} className="animate-spin" />
          ) : recording ? (
            <MicOff size={16} />
          ) : (
            <Mic size={16} />
          )}
          <span className="voice-btn-label">
            {recording
              ? (isAr ? `تسجيل… ${elapsed}s` : `Recording… ${elapsed}s`)
              : prepping
              ? (isAr ? "جاري طلب الإذن…" : "Requesting permission…")
              : busy
              ? (isAr ? "جاري النسخ…" : "Transcribing…")
              : (isAr ? "أو سجّل رسالتك صوتياً 🎙️" : "Or record your message 🎙️")}
          </span>
          {recording && <span className="voice-pulse" aria-hidden />}
        </button>
      </div>

      {/* Pre-permission priming tooltip — appears BEFORE the browser prompt on first click. */}
      {prepping && (
        <div className="voice-tooltip" role="status" data-testid="voice-permission-tooltip">
          <span dir="rtl" lang="ar">سنطلب إذن الميكروفون لتسجيل رسالتك</span>
          <span className="voice-tooltip-sep" aria-hidden>·</span>
          <span dir="ltr" lang="en">We'll request microphone access to record your message</span>
        </div>
      )}

      {state === "error" && errorMsg && (
        <div
          className="mt-2 text-xs px-3 py-2 rounded-lg"
          role="alert"
          data-testid="voice-error"
          style={{
            background: 'rgba(220, 38, 38, 0.08)',
            border: '1px solid rgba(220, 38, 38, 0.30)',
            color: '#7f1d1d',
            fontFamily: isAr ? 'Cairo' : 'Plus Jakarta Sans',
          }}
        >
          {errorMsg}
        </div>
      )}
      <style>{`
        .voice-guide {
          display: flex;
          flex-direction: column;
          gap: 4px;
          margin-bottom: 6px;
          padding: 10px 14px;
          border-radius: 14px;
          background: linear-gradient(135deg, rgba(212, 175, 55, 0.08), rgba(212, 175, 55, 0.02));
          border: 1px solid rgba(212, 175, 55, 0.22);
          color: #7a5d1e;
        }
        .voice-guide-line {
          font-family: ${isAr ? "'Cairo'" : "'Plus Jakarta Sans'"}, sans-serif;
          font-size: 12.5px;
          line-height: 1.55;
          font-weight: 500;
          opacity: .88;
        }
        .voice-guide-line[lang="ar"] { font-family: 'Cairo', sans-serif; text-align: right; }
        .voice-guide-line[lang="en"] { font-family: 'Plus Jakarta Sans', sans-serif; text-align: left; font-style: italic; opacity: .72; }

        .voice-tooltip {
          margin-top: 8px;
          padding: 8px 14px;
          border-radius: 999px;
          background: rgba(15, 36, 33, 0.92);
          color: #F5E9C8;
          font-family: 'Cairo', 'Plus Jakarta Sans', sans-serif;
          font-size: 12px;
          font-weight: 500;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
          border: 1px solid rgba(212, 175, 55, 0.35);
          box-shadow: 0 8px 22px -10px rgba(15, 36, 33, 0.5);
          animation: voiceTooltipIn .22s ease-out;
        }
        .voice-tooltip-sep {
          color: rgba(212, 175, 55, 0.55);
          font-weight: 700;
        }
        @keyframes voiceTooltipIn {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .voice-btn {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 10px 18px;
          border-radius: 999px;
          background: linear-gradient(135deg, #0f2421 0%, #1a3d38 100%);
          color: #F5E9C8;
          font-family: ${isAr ? "'Cairo'" : "'Plus Jakarta Sans'"}, sans-serif;
          font-weight: 600;
          font-size: 13px;
          border: 1px solid rgba(212, 175, 55, 0.35);
          transition: all .25s ease;
          box-shadow: 0 4px 14px -6px rgba(15, 36, 33, 0.4);
          cursor: pointer;
          position: relative;
        }
        .voice-btn:hover:not(:disabled) {
          border-color: #D4AF37;
          box-shadow: 0 6px 20px -6px rgba(212, 175, 55, 0.55);
          transform: translateY(-1px);
        }
        .voice-btn:disabled { opacity: .7; cursor: default; }
        .voice-btn-recording {
          background: linear-gradient(135deg, #7f1d1d 0%, #b91c1c 100%);
          border-color: rgba(255, 200, 200, 0.55);
          color: #FFF;
        }
        .voice-btn-busy { background: linear-gradient(135deg, #1a3d38 0%, #0f2421 100%); }
        .voice-btn-label { white-space: nowrap; }
        .voice-pulse {
          display: inline-block;
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background: #FF3B30;
          box-shadow: 0 0 0 0 rgba(255, 59, 48, .7);
          animation: voicePulse 1.4s cubic-bezier(0, 0, .2, 1) infinite;
        }
        @keyframes voicePulse {
          0%   { box-shadow: 0 0 0 0 rgba(255, 59, 48, .55); }
          70%  { box-shadow: 0 0 0 10px rgba(255, 59, 48, 0); }
          100% { box-shadow: 0 0 0 0 rgba(255, 59, 48, 0); }
        }
      `}</style>
    </div>
  );
}
